import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  onRetry: () => Promise<boolean>;
}

export function ChunkLoadError({ onRetry }: Props) {
  const [retrying, setRetrying] = useState(false);
  const [stillFailing, setStillFailing] = useState(false);
  const busy = useRef(false);

  const retry = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setRetrying(true);
    const reloading = await onRetry().catch(() => false);
    if (reloading) return;
    busy.current = false;
    setRetrying(false);
    setStillFailing(true);
  }, [onRetry]);

  useEffect(() => {
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  }, [retry]);

  return (
    <div role="alert" className="flex flex-col items-center justify-center gap-4 px-6 py-16 text-center">
      <WifiOff className="h-8 w-8 text-muted-foreground" aria-hidden />
      <h2 className="text-lg font-semibold">No pudimos cargar esta parte de la aplicación</h2>
      <p className="max-w-md text-sm text-muted-foreground">
        Suele pasar cuando la conexión a internet es inestable. Revisa tu conexión y vuelve a intentarlo.
      </p>
      {stillFailing && !retrying && (
        <p className="max-w-md text-sm text-destructive">
          Todavía no hay conexión. Inténtalo de nuevo en unos segundos.
        </p>
      )}
      <Button onClick={retry} disabled={retrying}>
        {retrying && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
        {retrying ? "Reintentando…" : "Reintentar"}
      </Button>
    </div>
  );
}
